import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Grid,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';

function Roles() {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#1976d2',
    is_default: false,
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/roles/');
      setRoles(response.data);
    } catch (err) {
      setError('Ошибка загрузки ролей');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/roles/permissions/categories');
      setPermissionCategories(response.data);
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  };

  const handleCreateOpen = () => {
    setFormData({
      name: '',
      description: '',
      color: '#1976d2',
      is_default: false,
      permissions: []
    });
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    try {
      await axios.post('/roles/', formData);
      setCreateDialogOpen(false);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка создания роли');
    }
  };

  const handleEditOpen = async (role) => {
    try {
      const response = await axios.get(`/roles/${role.id}`);
      setSelectedRole(response.data);
      setFormData({
        name: response.data.name,
        description: response.data.description || '',
        color: response.data.color || '#1976d2',
        is_default: response.data.is_default,
        permissions: response.data.permissions || []
      });
      setEditDialogOpen(true);
    } catch (err) {
      setError('Ошибка загрузки роли');
    }
  };

  const handleEditSubmit = async () => {
    try {
      await axios.put(`/roles/${selectedRole.id}`, formData);
      setEditDialogOpen(false);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка обновления роли');
    }
  };

  const handleDeleteOpen = (role) => {
    setSelectedRole(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSubmit = async () => {
    try {
      await axios.delete(`/roles/${selectedRole.id}`);
      setDeleteDialogOpen(false);
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления роли');
    }
  };

  const handlePermissionChange = (permCode) => {
    setFormData(prev => {
      const permissions = prev.permissions.includes(permCode)
        ? prev.permissions.filter(p => p !== permCode)
        : [...prev.permissions, permCode];
      return { ...prev, permissions };
    });
  };

  const handleCategoryToggle = (category) => {
    setFormData(prev => {
      const categoryPerms = category.permissions.map(p => p.code);
      const allSelected = categoryPerms.every(p => prev.permissions.includes(p));

      let newPermissions;
      if (allSelected) {
        // Remove all from category
        newPermissions = prev.permissions.filter(p => !categoryPerms.includes(p));
      } else {
        // Add all from category
        newPermissions = [...new Set([...prev.permissions, ...categoryPerms])];
      }

      return { ...prev, permissions: newPermissions };
    });
  };

  const isCategoryFullySelected = (category) => {
    return category.permissions.every(p => formData.permissions.includes(p.code));
  };

  const isCategoryPartiallySelected = (category) => {
    const selectedCount = category.permissions.filter(p => formData.permissions.includes(p.code)).length;
    return selectedCount > 0 && selectedCount < category.permissions.length;
  };

  const renderPermissionsEditor = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Права доступа
      </Typography>

      {permissionCategories.map((category) => (
        <Accordion key={category.name} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <FormControlLabel
              onClick={(e) => e.stopPropagation()}
              control={
                <Checkbox
                  checked={isCategoryFullySelected(category)}
                  indeterminate={isCategoryPartiallySelected(category)}
                  onChange={() => handleCategoryToggle(category)}
                />
              }
              label={
                <Typography variant="subtitle2">
                  {category.name}
                </Typography>
              }
            />
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              {category.permissions.map((perm) => (
                <Grid item xs={12} sm={6} md={4} key={perm.code}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.permissions.includes(perm.code)}
                        onChange={() => handlePermissionChange(perm.code)}
                        size="small"
                      />
                    }
                    label={
                      <Tooltip title={perm.description || ''} placement="top">
                        <Typography variant="body2">{perm.name}</Typography>
                      </Tooltip>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Роли и права</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateOpen}
        >
          Создать роль
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Роль</TableCell>
                  <TableCell>Описание</TableCell>
                  <TableCell>Права</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                          label={role.name}
                          size="small"
                          sx={{
                            backgroundColor: role.color || '#757575',
                            color: 'white'
                          }}
                        />
                        {role.is_default && (
                          <Tooltip title="Роль по умолчанию">
                            <StarIcon sx={{ ml: 1, color: 'warning.main', fontSize: 18 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{role.description || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {role.permissions?.length || 0} прав
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {role.is_system ? (
                        <Chip label="Системная" size="small" color="primary" variant="outlined" />
                      ) : (
                        <Chip label="Пользовательская" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Редактировать">
                        <IconButton onClick={() => handleEditOpen(role)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {!role.is_system && (
                        <Tooltip title="Удалить">
                          <IconButton color="error" onClick={() => handleDeleteOpen(role)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Создать роль</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Название"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Описание"
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              label="Цвет"
              type="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              sx={{ width: 100 }}
              InputProps={{ sx: { height: 56 } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                />
              }
              label="Роль по умолчанию для новых пользователей"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {renderPermissionsEditor()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleCreateSubmit} variant="contained">Создать</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Редактировать роль</DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Название"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            disabled={selectedRole?.is_system}
          />
          <TextField
            margin="normal"
            fullWidth
            label="Описание"
            multiline
            rows={2}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              label="Цвет"
              type="color"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              sx={{ width: 100 }}
              InputProps={{ sx: { height: 56 } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                />
              }
              label="Роль по умолчанию для новых пользователей"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {renderPermissionsEditor()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleEditSubmit} variant="contained">Сохранить</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Удалить роль</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить роль <strong>{selectedRole?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Убедитесь, что роль не назначена пользователям.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
          <Button onClick={handleDeleteSubmit} variant="contained" color="error">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Roles;
